import { FC, useContext } from 'react';
import { Styled } from './index.styles';
import { serverData } from '../../organisms/HotelInformation/HotelInformation';
import moment from 'moment';
import { NAVIGATION_URL_CONSTANTS } from '../../organisms/HotelInformation/config';
import { PageContext } from '../../context/PageContext';
import { useCheckBreakpoint } from '../../hooks';
import clsx from 'clsx';
import { useHQVContext } from '../../context';
import { hardCodedAllLocale } from '../../constants';
import { addSubDirectoryPrefix, getTimeInArabicTimeOfDayFormat } from '../../utils/src/helper';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const HotelInformation: FC<{ hotelInformationData: any }> = ({ hotelInformationData }) => {
  const { jsonContent } = useContext(serverData);
  const pageContext = useContext(PageContext);
  const { hqvState } = useHQVContext();

  const hotelData = hotelInformationData?.property;
  const accessibility = jsonContent?.accessibilityLabel;

  // get dateFormat for different locales
  let checkInDate = '';
  let checkOutDate = '';
  const localeCn = hardCodedAllLocale?.cn;

  switch (pageContext?.currentLocale) {
    case hardCodedAllLocale?.en:
      checkInDate = moment(hotelData?.policies?.checkInTime, 'HH:mm a').format('hh:mm A');
      checkOutDate = moment(hotelData?.policies?.checkOutTime, 'HH:mm a').format('hh:mm A');
      break;
    case hardCodedAllLocale?.cn:
      moment.locale(localeCn.toLowerCase());
      checkInDate = moment(hotelData?.policies?.checkInTime, 'HH:mm').format('Ahh:mm');
      checkOutDate = moment(hotelData?.policies?.checkOutTime, 'HH:mm').format('Ahh:mm');
      break;
    case hardCodedAllLocale?.ar:
      checkInDate = getTimeInArabicTimeOfDayFormat(hotelData?.policies?.checkInTime);
      checkOutDate = getTimeInArabicTimeOfDayFormat(hotelData?.policies?.checkOutTime);
      break;
    default:
      checkInDate = hotelData?.policies?.checkInTime ? hotelData?.policies?.checkInTime : '';
      checkOutDate = hotelData?.policies?.checkOutTime ? hotelData?.policies?.checkOutTime : '';
      break;
  }

  const smokeFreePolicyText = jsonContent?.hotelInfoListItems[0]?.hotelInfoText;
  const smokeFreeLink = jsonContent?.hotelInfoListItems[0]?.hotelInfoLink;
  const clickTrackingLoc = 'HQV AEM Page';

  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportM = useCheckBreakpoint('viewportM');

  return (
    <Styled>
      <div className={clsx('t-font-m', 'information-box', `${hqvState.modalOpenState ? 'hqv-modal' : ''}`)}>
        <div className={clsx('col', 'm-icon-text', 'left-col-item')}>
          <span
            className={clsx(
              isViewportL ? 'icon-m' : 'icon-s',
              'icon-clock',
              'icon-decorative',
              'check-in',
              'left-col-icon'
            )}
          ></span>
          <span className={clsx(isViewportL ? 't-font-m' : isViewportM ? 't-font-s' : 't-font-xs', 'left-col-text')}>
            {jsonContent?.checkInLabel}: {hotelData?.policies ? checkInDate : ''}
          </span>
        </div>

        <div className={clsx('col', 'm-icon-text', 'left-col-item')}>
          <span
            className={clsx(
              isViewportL ? 'icon-m' : 'icon-s',
              'icon-clock',
              'icon-decorative',
              'check-out',
              'left-col-icon'
            )}
          ></span>
          <span className={clsx(isViewportL ? 't-font-m' : isViewportM ? 't-font-s' : 't-font-xs', 'left-col-text')}>
            {jsonContent?.checkOutLabel}: {hotelData?.policies ? checkOutDate : ''}
          </span>
        </div>

        {hotelData && (
          <div className={clsx('col', 'm-icon-text', 'left-col-item')}>
            {!hotelData?.policies?.smokefree && jsonContent?.noSmokeFreePolicyText && (
              <>
                <span
                  className={clsx(
                    isViewportL ? 'icon-m' : 'icon-s',
                    'icon-opt-out',
                    'icon-decorative',
                    'smoke-free',
                    'left-col-icon'
                  )}
                ></span>
                <span
                  className={clsx(isViewportL ? 't-font-m' : isViewportM ? 't-font-s' : 't-font-xs', 'left-col-text')}
                >
                  {jsonContent?.noSmokeFreePolicyText}
                </span>
              </>
            )}
            {hotelData?.policies?.smokefree && smokeFreePolicyText && (
              <>
                <span
                  className={clsx(
                    isViewportL ? 'icon-m' : 'icon-s',
                    'icon-opt-out',
                    'icon-decorative',
                    'smoke-free',
                    'left-col-icon'
                  )}
                ></span>
                <span
                  className={clsx(
                    isViewportL ? 't-subtitle-m' : isViewportM ? 't-label-alt-s' : 't-label-alt-xs',
                    'left-col-text',
                    'bold-item'
                  )}
                >
                  <a
                    href={smokeFreeLink}
                    target="_blank"
                    rel="noreferrer"
                    className="custom_click_track"
                    {...{ custom_click_track_value: `${clickTrackingLoc}| Smoke free policy |internal` }}
                  >
                    {smokeFreePolicyText}
                    <span role="note" aria-label="Opens in a new browser window."></span>
                  </a>
                </span>
              </>
            )}
          </div>
        )}
        {!jsonContent?.isPropertyDetailsSectionDisabled && (
          <div className={clsx('col', 'm-icon-text', 'left-col-item')}>
            <span
              className={clsx(
                isViewportL ? 'icon-m' : 'icon-s',
                'icon-decorative',
                'icon-accessibility',
                'check-out',
                'left-col-icon'
              )}
            ></span>
            <span
              className={clsx(
                isViewportL ? 't-subtitle-m' : isViewportM ? 't-label-alt-s' : 't-label-alt-xs',
                'left-col-text',
                'bold-item'
              )}
            >
              <a
                href="#hqv-property-details-section"
                className="custom_click_track"
                {...{ custom_click_track_value: `${clickTrackingLoc}| Accessiblity link clicked |internal` }}
              >
                {accessibility}
              </a>
            </span>
          </div>
        )}
        {jsonContent?.isPropertyDetailsSectionDisabled && (
          <div className={clsx('col', 'm-icon-text', 'left-col-item')}>
            <span
              className={clsx(
                isViewportL ? 'icon-m' : 'icon-s',
                'icon-decorative',
                'icon-accessibility',
                'check-out',
                'left-col-icon'
              )}
            ></span>
            <span
              className={clsx(
                isViewportL ? 't-subtitle-m' : isViewportM ? 't-label-alt-s' : 't-label-alt-xs',
                'left-col-text',
                'bold-item'
              )}
            >
              <a
                href={
                  addSubDirectoryPrefix(NAVIGATION_URL_CONSTANTS.SM_VIEW_HOTEL_WEBSITE_LINK) +
                  hotelData?.id?.toLowerCase() +
                  '-' +
                  hotelData?.basicInformation?.name
                    .replace('-', ' ')
                    .replace(/([~!@#$%^&*()_+=`{}[\]|\\:;'<>,./? ])+/g, '-')
                    .replace(/^(-)+|(-)+$/g, '')
                    ?.toLowerCase()
                }
                target="_blank"
                rel="noreferrer"
                className="custom_click_track"
                {...{ custom_click_track_value: `${clickTrackingLoc}| Accessiblity link clicked |internal` }}
              >
                {accessibility}
              </a>
            </span>
          </div>
        )}
      </div>
    </Styled>
  );
};

export default HotelInformation;
